import React, { useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';

const questions = [
  {
    id: 1,
    question: "Which exercise intervention is most effective for improving balance in older adults?",
    options: [
      "Aerobic walking",
      "Resistance training",
      "Tai Chi",
      "Stretching exercises"
    ],
    correct: "Tai Chi",
    explanation: "Tai Chi has been proven most effective for balance improvement due to its focus on weight shifting, postural alignment, and coordinated movements."
  },
  {
    id: 2,
    question: "What is the recommended frequency for balance training exercises?",
    options: [
      "Once a week",
      "Twice a week",
      "Three times a week",
      "Daily"
    ],
    correct: "Three times a week",
    explanation: "Three times per week is the recommended frequency for balance training to maintain effectiveness while allowing adequate recovery time."
  },
  {
    id: 3,
    question: "Which environmental modification has the highest impact on fall prevention?",
    options: [
      "Installing grab bars",
      "Improving lighting",
      "Removing throw rugs",
      "Adding non-slip mats"
    ],
    correct: "Improving lighting",
    explanation: "Adequate lighting is crucial as it helps identify potential hazards and supports better visual processing for safe navigation."
  },
  {
    id: 4,
    question: "What is the most appropriate intervention for medication-related fall risks?",
    options: [
      "Immediately stopping all medications",
      "Gradual medication review and adjustment",
      "Adding more medications for balance",
      "Switching to all generic medications"
    ],
    correct: "Gradual medication review and adjustment",
    explanation: "A systematic review and gradual adjustment of medications, especially those affecting balance or blood pressure, is the safest approach."
  }
];

const InterventionQuiz = ({ onComplete }) => {
  const [hasCalledComplete, setHasCalledComplete] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleAnswer = (event) => {
    setAnswers({
      ...answers,
      [currentQuestion]: event.target.value
    });
    setShowExplanation(true);
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const calculateScore = () => {
    let score = 0;
    Object.keys(answers).forEach(questionIndex => {
      if (answers[questionIndex] === questions[questionIndex].correct) {
        score++;
      }
    });
    return (score / questions.length) * 100;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    setHasCalledComplete(false);
  };

  if (showResult) {
    const score = calculateScore();
    if (score >= 70 && onComplete && !hasCalledComplete) {
      setHasCalledComplete(true);
      onComplete();
    }
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Intervention Knowledge Check Complete!</Typography>
          <Typography variant="body1" gutterBottom>
            Your score: {score}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={score} 
            sx={{ my: 2, height: 10, borderRadius: 5 }}
          />
          {score >= 70 ? (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Congratulations! You've passed the quiz. You can now proceed to the next section.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              You need a score of 70% or higher to proceed. Please try again.
            </Alert>
          )}
          <Button 
            variant="contained" 
            onClick={resetQuiz}
            sx={{ mt: 2 }}
          >
            Retake Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fall Prevention Interventions Quiz
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={(currentQuestion / questions.length) * 100} 
          sx={{ mb: 3, height: 10, borderRadius: 5 }}
        />
        
        <Typography variant="body1" gutterBottom>
          Question {currentQuestion + 1} of {questions.length}
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          {question.question}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={answers[currentQuestion] || ''}
            onChange={handleAnswer}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {showExplanation && (
          <Alert 
            severity={answers[currentQuestion] === question.correct ? "success" : "error"}
            sx={{ mt: 2, mb: 2 }}
          >
            {answers[currentQuestion] === question.correct ? "Correct! " : "Incorrect. "}
            {question.explanation}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!answers[currentQuestion]}
          sx={{ mt: 2 }}
        >
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Show Results'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default InterventionQuiz;
